import axios from "axios";

// Deliberately a bare axios call, not the shared `api` client: these pages are
// opened by members of the public straight from an email or a text, with no session.
// The shared client attaches CSRF/auth interceptors and can bounce a caller to the
// login screen, which would be wrong here.
const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const SMS_PATH = (code) => `/notifications/sms/stop/${encodeURIComponent(code)}/`;
const EMAIL_PATH = "/notifications/email/unsubscribe/";

// The backend owns the wording, so the page and the backend's own HTML fallback
// (hit when an email links straight at the API host) can't drift apart. Both
// endpoints answer with {status, title, message} - on 400 as well as 200.
const isResult = (data) =>
  !!data && typeof data === "object" && typeof data.status === "string" && !!data.message;

// The same URL serves a full HTML page when JSON isn't asked for. If we get that back
// - an older backend that doesn't know ?format=json yet, say, mid-deploy - send the
// browser there rather than showing an error on top of a page that works.
const fallback = (path, query) => {
  const qs = new URLSearchParams(query).toString();
  return { status: "redirect", url: `${API_BASE}${path}${qs ? `?${qs}` : ""}` };
};

const request = async (path, query) => {
  try {
    const { data } = await axios.get(`${API_BASE}${path}`, {
      params: { ...query, format: "json" },
    });
    return isResult(data) ? data : fallback(path, query);
  } catch (error) {
    const data = error?.response?.data;
    if (isResult(data)) return data;
    // A response we couldn't read (HTML body, unexpected shape) still means the
    // backend is reachable, so let it render its own page. No response at all is a
    // real error and belongs to the caller.
    if (error?.response) return fallback(path, query);
    throw error;
  }
};

export const optOutOfSms = (code) => request(SMS_PATH(code), {});

export const unsubscribeFromEmail = (token) => request(EMAIL_PATH, { token });

// Short links inside a text (/l/<code>). The backend answers {status, url} and
// the public page sends the browser on - the same shape and the same reason as
// the opt-out endpoints above: the short domain is the React app, not the API.
export const resolveShortLink = async (code) => {
  const { data } = await axios.get(`${API_BASE}/l/${encodeURIComponent(code)}/`, {
    params: { format: "json" },
    // A 404 is a real answer here (unknown code), not a transport failure, and
    // it still carries somewhere sensible to send the visitor.
    validateStatus: (status) => status < 500,
  });
  return data && typeof data.url === "string" ? data : null;
};

// Tracked links (/k/<code>). Same shape and the same reason as the short links
// above, with one difference that matters: an email click arrives here carrying
// the campaign's UTM tags (the backend tags an email AFTER the link is minted),
// so they are forwarded to the backend to be put back on the destination.
// Dropping them would break Analytics > Campaign Leads for every tracked click.
export const resolveTrackedLink = async (code, search = "") => {
  const carried = Object.fromEntries(new URLSearchParams(search || "").entries());
  delete carried.format;
  const { data } = await axios.get(`${API_BASE}/k/${encodeURIComponent(code)}/`, {
    params: { ...carried, format: "json" },
    validateStatus: (status) => status < 500,
  });
  return data && typeof data.url === "string" ? data : null;
};
